import React from 'react';
import { db } from '../../config-firebase/firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import { setDoc, updateDoc } from '../../api/FirestoreInterceptor';
import { deleteData, getOneItemColleciton, registerDailyStockMovement } from '../../api/Api.js';
import { isOrderFullyFinished } from '../../Helpers/Helpers.js';
import { updateIngredientsStock } from './stockOperations';
import style from '../../assets/styles/RequestListToBePrepared.module.scss';

/**
 * Hook para gerenciar operações de pagamento, entrega pelo garçom,
 * finalização e cálculo de status/cor de pedidos no PDV.
 */
export const usePdvPaymentAndDelivery = ({
  global,
  canCancelOrders,
  setRequestDoneList,
  setLoadingAvailableMenuDishes,
  setShowPaymentBlockedPopup,
  setSelectedItemToPay,
}) => {
  const [showFinalizarMessage, setShowFinalizarMessage] = React.useState(false);
  const [selectedItemToFinalize, setSelectedItemToFinalize] = React.useState(null);
  const [ShowDefaultMessage, setShowDefaultMessage] = React.useState(false);
  const [selectedRequestId, setSelectedRequestId] = React.useState(null);
  const [payingOrders, setPayingOrders] = React.useState({});

  const openFinalizarModal = (item) => {
    setShowFinalizarMessage(true);
    setSelectedItemToFinalize(item);
  };

  const closeFinalizarModal = () => {
    setShowFinalizarMessage(false);
    setSelectedItemToFinalize(null);
  };

  const orderDelivery = async (item) => {
    try {
      const requestRef = doc(db, 'requests', item.id);
      await updateDoc(requestRef, {
        orderDelivered: true,
        done: false,
      });

      console.log('Document marked as delivered. UI should update via listener.');

      try {
        if (item.name === 'anonimo' || item.name === 'anonymous') {
          if (item.idUser) {
            const userRef = doc(db, 'user', item.idUser);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              await deleteData('user', item.idUser);
            }
          }
        } else if (item.idUser) {
          const userRef = doc(db, 'user', item.idUser);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            await updateDoc(userRef, { request: [] });
          } else {
            console.warn(`[WARNING] Documento de usuário ${item.idUser} não existe, pulando limpeza.`);
          }
        }
      } catch (userError) {
        console.error('Erro ao limpar carrinho do usuário (não bloqueante):', userError);
      }

      if (global.hasRawMaterial) {
        await updateIngredientsStock(item, { global, setLoadingAvailableMenuDishes });
        await registerDailyStockMovement('Venda (Baixa Automática)');
        console.log('Stock updated successfully!');
      } else {
        console.log('Stock update skipped (Basic Package)');
      }
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      alert('Houve um erro ao finalizar o pedido, mas os dados básicos foram salvos.');
    }
  };

  const confirmFinalizarPedido = async () => {
    const itemToFinalize = selectedItemToFinalize;
    closeFinalizarModal();
    if (itemToFinalize) {
      await orderDelivery(itemToFinalize);
    }
  };

  const getPostpaidButtonState = (item) => {
    if (!item.request || item.request.length === 0)
      return { label: 'PENDENTE', color: 'red', className: style.pendent };

    const hasToDeliver = item.request.some((req) => req.pronto && !req.entregue);
    const allDelivered = item.request.every((req) => req.pronto && req.entregue);

    if (hasToDeliver) {
      return {
        label: 'NOVA ENTREGA',
        color: '#b45309',
        className: `${style.pendent} ${style.blinkingDeliveryBtn}`,
        inlineStyle: { backgroundColor: '#b45309', color: '#ffffff', fontWeight: 'bold' },
      };
    } else if (allDelivered) {
      return { label: 'PRONTO', color: 'green', className: style.done, inlineStyle: {} };
    } else {
      return { label: 'PENDENTE', color: 'red', className: style.pendent, inlineStyle: {} };
    }
  };

  const handleWaiterDelivery = async (item) => {
    const currentState = getPostpaidButtonState(item);
    if (currentState.label === 'NOVA ENTREGA') {
      const updatedRequests = item.request.map((req) => {
        if (req.pronto && !req.entregue) {
          return { ...req, entregue: true, deliveredByWaiter: true, status: 'Entregue' };
        }
        return req;
      });
      item.request = updatedRequests;

      const allDelivered = updatedRequests.every((req) => req.pronto && req.entregue);
      if (allDelivered) {
        item.done = false;
      } else {
        item.done = true;
      }

      try {
        await setDoc(doc(db, 'requests', item.id), item);

        if (item.idUser) {
          const userDocRef = doc(db, 'user', item.idUser);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.request && Array.isArray(userData.request)) {
              const newUserDataRequests = userData.request.map((uReq) => {
                const isParentMatch = !uReq.parentRequestId || uReq.parentRequestId === item.id;
                if (isParentMatch) {
                  const match = updatedRequests.find(
                    (r) =>
                      (r.indexInRequest !== undefined &&
                        uReq.indexInRequest !== undefined &&
                        r.indexInRequest === uReq.indexInRequest) ||
                      (r.name === uReq.name && r.entregue)
                  );
                  if (match) {
                    const statusLabel = match.entregue
                      ? 'Entregue'
                      : match.pronto
                      ? 'Pronto'
                      : match.status || uReq.status || '';
                    return {
                      ...uReq,
                      ...match,
                      entregue: Boolean(match.entregue),
                      deliveredByWaiter: Boolean(match.deliveredByWaiter),
                      pronto: Boolean(match.pronto),
                      status: statusLabel,
                    };
                  }
                }
                return uReq;
              });
              await updateDoc(userDocRef, { request: newUserDataRequests });
            }
          }
        }
        console.log('Entregas atualizadas com sucesso');
      } catch (err) {
        console.error('Erro ao atualizar entregas', err);
      }
    }
  };

  const handleDeleteRequest = async (id) => {
    if (!canCancelOrders) {
      alert('Você não tem permissão para cancelar pedidos. Consulte o gerente.');
      setShowDefaultMessage(false);
      return;
    }
    try {
      const data = await getOneItemColleciton('requests', id);
      if (data) {
        await deleteData('requests', id);
        if (data.name === 'anonimo' || data.name === 'anonymous') {
          if (data.idUser) {
            const userRef = doc(db, 'user', data.idUser);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              await deleteData('user', data.idUser);
            }
          }
        } else if (data.idUser) {
          const userRef = doc(db, 'user', data.idUser);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            await updateDoc(userRef, { request: [] });
          } else {
            console.warn(`[WARNING] Documento de usuário ${data.idUser} não existe, pulando limpeza.`);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao deletar/cancelar pedido:', error);
    } finally {
      setShowDefaultMessage(false);
    }
  };

  const openShowModal = (id) => {
    if (!canCancelOrders) {
      alert('Você não tem permissão para cancelar pedidos. Consulte o gerente.');
      return;
    }
    setShowDefaultMessage(true);
    setSelectedRequestId(id);
  };

  const closeModal = () => {
    setShowDefaultMessage(false);
  };

  const RequestDone = async (item) => {
    try {
      await updateDoc(doc(db, 'requests', item.id), { done: false });
      setRequestDoneList((prevList) =>
        prevList.map((req) =>
          req.id === item.id ? { ...req, done: false } : req
        )
      );
      console.log('Document successfully updated !');
    } catch (error) {
      console.error(error);
    }
  };

  const handlePaymentMethodChange = async (method, item) => {
    try {
      await updateDoc(doc(db, 'requests', item.id), {
        paymentMethod: method,
      });
      setRequestDoneList((prevList) =>
        prevList.map((req) =>
          req.id === item.id ? { ...req, paymentMethod: method } : req
        )
      );
      console.log('Payment method updated successfully:', method);
    } catch (error) {
      console.error('Error updating payment method:', error);
    }
  };

  const descontFinalPrice = async (descont, idRequest) => {
    try {
      const requestRef = doc(db, 'requests', idRequest);
      const requestSnap = await getDoc(requestRef);

      if (requestSnap.exists()) {
        const currentFinalPrice = requestSnap.data().finalPriceRequest;
        const updatedFinalPrice = currentFinalPrice - descont;
        await updateDoc(requestRef, {
          finalPriceRequest: updatedFinalPrice,
        });
        console.log('finalPriceRequest atualizado com sucesso para:', updatedFinalPrice);
      } else {
        console.log('Documento não encontrado!');
      }
    } catch (error) {
      console.error('Erro ao atualizar finalPriceRequest:', error);
    }
  };

  const unlockPayment = async (item) => {
    if (!item) return;
    try {
      const docRef = doc(db, 'requests', item.id);
      await updateDoc(docRef, {
        'paymentCall.active': false,
        'paymentCall.tableNumber': item.tableNumber || '',
        'paymentCall.callerName': item.name || 'Cliente',
      });
      setShowPaymentBlockedPopup(false);
      setSelectedItemToPay(null);
    } catch (err) {
      console.error('Erro ao desbloquear pagamento:', err);
    }
  };

  const changeStatusPaid = async (item) => {
    if (!item?.id || payingOrders[item.id]) return;

    setPayingOrders((prev) => ({ ...prev, [item.id]: true }));

    try {
      const updates = {
        paymentDone: true,
      };

      if (item.idUser) {
        try {
          const userRef = doc(db, 'user', item.idUser);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const currentCredit = parseFloat(userData.credit || 0);
            if (currentCredit > 0) {
              const orderTotal = parseFloat(item.finalPriceRequest || 0);
              const creditUsed = Math.min(currentCredit, orderTotal);
              const creditRemaining = Math.max(0, currentCredit - creditUsed);

              updates.creditUsed = Number(creditUsed.toFixed(2));
              updates.creditRemaining = Number(creditRemaining.toFixed(2));
              updates.previousCredit = Number(currentCredit.toFixed(2));

              await updateDoc(userRef, {
                credit: creditRemaining > 0 ? Number(creditRemaining.toFixed(2)) : 0,
              });

              console.log(
                `[CRÉDITO DEBITADO] Cliente: ${userData.name || 'Cliente'}, Crédito anterior: R$ ${currentCredit.toFixed(
                  2
                )}, Usado: R$ ${creditUsed.toFixed(2)}, Restante: R$ ${creditRemaining.toFixed(2)}`
              );
            }
          }
        } catch (creditErr) {
          console.error('Erro ao processar débito de crédito do cliente:', creditErr);
        }
      }

      await updateDoc(doc(db, 'requests', item.id), updates);

      setRequestDoneList((prevList) =>
        prevList.map((req) =>
          req.id === item.id ? { ...req, ...updates } : req
        )
      );

      global.setUserNewRequest({ ...item, ...updates });
      console.log('Order status paid successfully updated !');
    } catch (error) {
      console.error('Error updating status paid:', error);
    } finally {
      setPayingOrders((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const getStatusAndColor = (item) => {
    const fullyFinished = isOrderFullyFinished(item);

    const hasToDeliver = item.request && item.request.some((req) => req.pronto && !req.entregue);
    if (hasToDeliver) return { status: 'Nova Entrega', color: '#b45309' };

    if (!item?.paymentDone) return { status: 'Não pago', color: 'red' };
    if (item?.paymentDone === false && item?.done === true)
      return { status: 'Não pago', color: 'red' };

    if (item?.paymentDone === true) {
      if (item?.done === false || fullyFinished) {
        return { status: 'Feito', color: 'green' };
      }
      return { status: 'Pago', color: 'yellow' };
    }

    return { status: '', color: 'black' };
  };

  return {
    showFinalizarMessage,
    selectedItemToFinalize,
    openFinalizarModal,
    closeFinalizarModal,
    confirmFinalizarPedido,
    orderDelivery,
    getPostpaidButtonState,
    handleWaiterDelivery,
    ShowDefaultMessage,
    setShowDefaultMessage,
    selectedRequestId,
    openShowModal,
    closeModal,
    handleDeleteRequest,
    RequestDone,
    handlePaymentMethodChange,
    descontFinalPrice,
    unlockPayment,
    changeStatusPaid,
    payingOrders,
    getStatusAndColor,
  };
};
